import { SOURCE_LABELS, type ImportJob } from '../api/portfolio-import';
import type { ExchangeConnection, ExchangeFreshnessEntry } from '../api/exchange';
import type { ApiConnection } from '../api/exchange-sync';
import type { Wallet } from '../types';
import {
  type ExchangeBlockerClassification,
  type ExchangeFreshnessBucket,
  type ExchangeFreshnessState,
  type ExchangeHealthBlocker,
  type ExchangeHealthReason,
  type ExchangeHealthState,
  type ExchangePrimaryNextStep,
  resolveExchangeHealth,
} from './exchangeHealth';
import {
  AUTH_SESSION_BLOCKED_MESSAGE,
  SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE,
} from './sourceProof';
import type { SetupHandoffContext } from './setupHandoff';

export type CoverageResourceStatus = 'loading' | 'ready' | 'error';
export type CoverageSummarySurface = 'setup' | 'monitoring';
export type CoverageSourceKey = 'wallets' | 'csv' | 'exchanges';

export interface CoverageExchangeConnection {
  id: string;
  exchange: string;
  label: string | null;
  status: 'active' | 'error' | 'revoked';
  lastSyncedAt: string | null;
  lastSuccessfulSyncAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  healthState: ExchangeHealthState | null;
  healthReason: ExchangeHealthReason | null;
  healthBlocker: ExchangeHealthBlocker;
  blockerClassification: ExchangeBlockerClassification;
  freshnessBucket: ExchangeFreshnessBucket | null;
  freshnessStaleHours: number | null;
  freshnessState: ExchangeFreshnessState | null;
  primaryNextStep: ExchangePrimaryNextStep;
  lastSyncImportedCount: number | null;
  lastSyncSkippedCount: number | null;
  lastSyncErrorCount: number | null;
  lastSyncSourceLabel: string | null;
}

export interface CoverageProofLine {
  key: CoverageSourceKey;
  headline: string;
  status: 'included' | 'stale' | 'unproven';
  tone: 'info' | 'success' | 'warning' | 'error';
  meta: string;
}

export interface CoverageProofFact {
  key: 'latest-source' | 'completed-at' | 'outcome' | 'blocker-truth' | 'freshness-delta' | 'coverage-delta';
  label: string;
  value: string;
  tone?: 'info' | 'success' | 'warning' | 'error';
  mono?: boolean;
}

export interface CoverageSummaryHelper {
  tone: 'info' | 'warning' | 'error';
  eyebrow: string;
  message: string;
}

export type CoverageSummaryAction =
  | {
      kind: 'retry-sync' | 'manage-exchange' | 'review-import' | 'review-wallets' | 'inspect-analytics';
      label: string;
      connectionId?: string;
      exchange?: string;
    }
  | {
      kind: 'review-coverage';
      label: string;
      href: '/wallet-hub' | '/import' | '/exchange-sync';
    }
  | {
      kind: 'review-portfolio';
      label: 'Review Portfolio';
    }
  | {
      kind: 'review-source';
      label: string;
      href: '/wallet-hub' | '/import' | '/exchange-sync';
      sourceKey: CoverageSourceKey;
      connectionId?: string;
      exchange?: string;
    };

export interface PortfolioCoverageSummary {
  surface: CoverageSummarySurface;
  tone: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  lines?: CoverageProofLine[];
  facts?: CoverageProofFact[];
  action?: CoverageSummaryAction;
  helper?: CoverageSummaryHelper;
  isSkeleton?: boolean;
  hasProof?: boolean;
}

export function getCoverageSummaryFactValue(summary: PortfolioCoverageSummary, key: string) {
  return summary.facts?.find((fact) => fact.key === key)?.value ?? null;
}

export interface PortfolioCoverageSummaryInput {
  wallets: Wallet[];
  walletsStatus: CoverageResourceStatus;
  importJobs: ImportJob[];
  importStatus: CoverageResourceStatus;
  exchangeConnections: CoverageExchangeConnection[];
  exchangeStatus: CoverageResourceStatus;
  freshness: ExchangeFreshnessEntry[];
  freshnessStatus: CoverageResourceStatus;
  setupHandoff: SetupHandoffContext | null;
  surface?: CoverageSummarySurface;
}

interface MonitoringProofCandidate {
  kind: 'csv-import' | 'api-sync';
  sourceLabel: string;
  occurredAt: string;
  imported: number;
  skipped: number;
  errors: number;
  healthState?: ExchangeHealthState | null;
  blocker?: ExchangeHealthBlocker;
  primaryNextStep?: ExchangePrimaryNextStep;
  connectionId?: string;
  exchange?: string;
  persisted: boolean;
}

interface MonitoringGap {
  key: CoverageSourceKey;
  label: string;
  href: '/wallet-hub' | '/import' | '/exchange-sync';
  connectionId?: string;
  exchange?: string;
}

const numberFormatter = new Intl.NumberFormat('en-US');

function formatCount(value: number | null | undefined) {
  return numberFormatter.format(value ?? 0);
}

function formatProofTimestamp(value: string | null | undefined) {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFreshnessAge(staleHours: number | null) {
  if (staleHours === null) return 'unknown age';
  if (staleHours >= 48) return `${Math.floor(staleHours / 24)}d stale`;
  if (staleHours >= 1) return `${staleHours}h stale`;
  return '<1h stale';
}

function getTimestampValue(value: string | null | undefined) {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function sortByMostRecent<T extends {
  created_at?: string | null;
  completed_at?: string | null;
  createdAt?: string | null;
  occurredAt?: string | null;
  lastSyncedAt?: string | null;
  lastSuccessfulSyncAt?: string | null;
}>(
  values: T[],
) {
  return [...values].sort((left, right) => {
    const leftTime = getTimestampValue(
      left.completed_at ??
        left.lastSuccessfulSyncAt ??
        left.lastSyncedAt ??
        left.createdAt ??
        left.created_at ??
        left.occurredAt ??
        null,
    );
    const rightTime = getTimestampValue(
      right.completed_at ??
        right.lastSuccessfulSyncAt ??
        right.lastSyncedAt ??
        right.createdAt ??
        right.created_at ??
        right.occurredAt ??
        null,
    );
    return rightTime - leftTime;
  });
}

function buildCsvSessionMeta(setupHandoff: Extract<SetupHandoffContext, { kind: 'csv-import' }>) {
  return [
    `session ${setupHandoff.sourceLabel}`,
    `imported ${formatCount(setupHandoff.imported)}`,
    `skipped ${formatCount(setupHandoff.skipped)}`,
    `errors ${formatCount(setupHandoff.errors)}`,
    formatProofTimestamp(setupHandoff.occurredAt),
  ].join(' · ');
}

function buildApiSessionMeta(setupHandoff: Extract<SetupHandoffContext, { kind: 'api-sync' }>) {
  return [
    `session ${setupHandoff.exchangeLabel}`,
    `imported ${formatCount(setupHandoff.imported)}`,
    `skipped ${formatCount(setupHandoff.skipped)}`,
    `errors ${formatCount(setupHandoff.errors)}`,
    formatProofTimestamp(setupHandoff.occurredAt),
  ].join(' · ');
}

function getMatchingFreshnessEntry(
  connection: CoverageExchangeConnection,
  freshness: ExchangeFreshnessEntry[],
) {
  const normalizedExchange = connection.exchange.toLowerCase();
  return freshness.find((entry) => (
    entry.connectionId === connection.id ||
    entry.exchange.toLowerCase() === normalizedExchange
  ));
}

function getExchangeRouteLabel(connection: Pick<CoverageExchangeConnection, 'label' | 'exchange'>) {
  return connection.label ?? connection.exchange.replace(/(^\w)|([_-]\w)/g, (match) => match.replace(/[_-]/, '').toUpperCase());
}

function getMonitoringActionLabel(gap: MonitoringGap) {
  if (gap.href === '/exchange-sync') {
    return gap.exchange ? `Review ${gap.label} in Exchange Sync` : 'Review Exchange Sync';
  }

  if (gap.href === '/wallet-hub') {
    return 'Review Wallet Coverage';
  }

  return gap.key === 'csv' ? 'Review Import Proof' : 'Review Import';
}

function getAggregateCoverageAction(gaps: MonitoringGap[]): CoverageSummaryAction {
  const preferredHref =
    gaps.some((gap) => gap.key === 'wallets')
      ? '/wallet-hub'
      : gaps.some((gap) => gap.href === '/exchange-sync')
        ? '/exchange-sync'
        : '/import';

  return {
    kind: 'review-coverage',
    label: 'Review Coverage',
    href: preferredHref,
  };
}

function getBlockerTruthFactValue({
  activeCandidate,
  blockedConnections,
  staleConnections,
  gaps,
}: {
  activeCandidate: MonitoringProofCandidate;
  blockedConnections: Array<CoverageExchangeConnection & { health: ReturnType<typeof resolveExchangeHealth> }>;
  staleConnections: Array<CoverageExchangeConnection & { health: ReturnType<typeof resolveExchangeHealth> }>;
  gaps: MonitoringGap[];
}): Pick<CoverageProofFact, 'value' | 'tone'> {
  const primaryBlockedConnection = blockedConnections[0] ?? null;

  if (primaryBlockedConnection) {
    const label = getExchangeRouteLabel(primaryBlockedConnection);

    if (primaryBlockedConnection.health.blockerClassification === 'auth_action') {
      return {
        value: `${label}: ${AUTH_SESSION_BLOCKED_MESSAGE}`,
        tone: 'warning',
      };
    }

    if (
      primaryBlockedConnection.health.blockerClassification === 'safe_exchange_replay_blocked' ||
      primaryBlockedConnection.health.runnerWaitState
    ) {
      return {
        value: `${label}: ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}`,
        tone: 'warning',
      };
    }

    if (primaryBlockedConnection.health.blockerClassification === 'credential_action') {
      return {
        value: `${label}: repair saved read-only credentials before Pinecurl can refresh live data while mounted proof remains truthful`,
        tone: 'warning',
      };
    }
  }

  if (activeCandidate.blocker === 'SAFE_EXCHANGE_REPLAY_BLOCKED') {
    return {
      value: `${activeCandidate.sourceLabel}: ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}`,
      tone: 'warning',
    };
  }

  if (staleConnections.length === 1 && blockedConnections.length === 0) {
    return {
      value: `${getExchangeRouteLabel(staleConnections[0])}: stale but verified proof remains mounted until a fresh sync completes`,
      tone: 'warning',
    };
  }

  if (activeCandidate.kind === 'csv-import' && (activeCandidate.skipped > 0 || activeCandidate.errors > 0)) {
    return {
      value: `${activeCandidate.sourceLabel}: imported proof remains mounted while skipped or error rows still need review on /import`,
      tone: 'warning',
    };
  }

  if (gaps.length > 1) {
    return {
      value: `${formatCount(gaps.length)} sources still need owner-route review while the mounted proof remains truthful`,
      tone: 'warning',
    };
  }

  return {
    value: 'No source is blocked. The mounted proof remains truthful on this monitoring route.',
    tone: 'success',
  };
}

function getExchangeTaxonomyLabel(connection: {
  blockerClassification: ExchangeBlockerClassification;
  freshnessState: ExchangeFreshnessState | null;
}) {
  if (connection.blockerClassification === 'safe_exchange_replay_blocked') {
    return 'SAFE_EXCHANGE_REPLAY_BLOCKED';
  }

  if (connection.blockerClassification === 'auth_action') {
    return 'auth required';
  }

  if (connection.blockerClassification === 'credential_action') {
    return 'credential action required';
  }

  if (connection.freshnessState === 'stale_verified') {
    return 'stale but verified';
  }

  return null;
}

export function normalizeExchangeConnection(connection: ExchangeConnection): CoverageExchangeConnection {
  return {
    id: connection.id,
    exchange: connection.exchange,
    label: connection.label,
    status: connection.status,
    lastSyncedAt: connection.last_synced_at,
    lastSuccessfulSyncAt: connection.last_successful_sync_at ?? connection.last_synced_at,
    errorMessage: connection.error_message,
    createdAt: connection.created_at,
    healthState: connection.sync_health_state ?? null,
    healthReason: connection.sync_health_reason ?? null,
    healthBlocker: connection.sync_health_blocker ?? null,
    blockerClassification: connection.sync_blocker_classification ?? null,
    freshnessBucket: connection.sync_freshness_bucket ?? null,
    freshnessStaleHours: connection.sync_freshness_stale_hours ?? null,
    freshnessState: connection.sync_freshness_state ?? null,
    primaryNextStep: connection.sync_primary_next_step ?? null,
    lastSyncImportedCount: connection.last_sync_imported_count ?? null,
    lastSyncSkippedCount: connection.last_sync_skipped_count ?? null,
    lastSyncErrorCount: connection.last_sync_error_count ?? null,
    lastSyncSourceLabel: connection.last_sync_source_label ?? null,
  };
}

export function normalizeApiConnection(connection: ApiConnection): CoverageExchangeConnection {
  return {
    id: connection.id,
    exchange: connection.exchange,
    label: null,
    status: connection.sync_status,
    lastSyncedAt: connection.last_synced_at,
    lastSuccessfulSyncAt: connection.last_sync_completed_at ?? connection.last_synced_at,
    errorMessage: connection.error_message,
    createdAt: connection.created_at,
    healthState: connection.health_state ?? null,
    healthReason: connection.health_reason ?? null,
    healthBlocker: connection.health_blocker ?? null,
    blockerClassification: connection.blocker_classification ?? null,
    freshnessBucket: connection.freshness_bucket ?? null,
    freshnessStaleHours: connection.freshness_stale_hours ?? null,
    freshnessState: connection.freshness_state ?? null,
    primaryNextStep: connection.primary_next_step ?? null,
    lastSyncImportedCount: connection.last_sync_imported_count ?? null,
    lastSyncSkippedCount: connection.last_sync_skipped_count ?? null,
    lastSyncErrorCount: connection.last_sync_error_count ?? null,
    lastSyncSourceLabel: connection.last_sync_source_label ?? null,
  };
}

function buildAnnotatedConnections(
  exchangeConnections: CoverageExchangeConnection[],
  freshness: ExchangeFreshnessEntry[],
) {
  return exchangeConnections.map((connection) => {
    const freshnessEntry = getMatchingFreshnessEntry(connection, freshness);
    const health = resolveExchangeHealth({
      surface: 'freshness',
      exchange: connection.exchange,
      connectionId: connection.id,
      lastSuccessfulSyncAt:
        freshnessEntry?.last_synced_at ??
        connection.lastSuccessfulSyncAt ??
        connection.lastSyncedAt,
      syncStatus: connection.status,
      errorMessage: connection.errorMessage,
      healthState: freshnessEntry?.health_state ?? connection.healthState,
      healthReason: freshnessEntry?.health_reason ?? connection.healthReason,
      healthBlocker: freshnessEntry?.health_blocker ?? connection.healthBlocker,
      blockerClassification: freshnessEntry?.blocker_classification ?? connection.blockerClassification,
      freshnessBucket:
        connection.freshnessBucket ??
        (freshnessEntry?.last_synced_at
          ? freshnessEntry.isStale
            ? 'stale'
            : 'fresh'
          : freshnessEntry
            ? 'never_synced'
            : null),
      freshnessStaleHours: freshnessEntry?.staleHours ?? connection.freshnessStaleHours,
      freshnessState: freshnessEntry?.freshness_state ?? connection.freshnessState,
      primaryNextStep: freshnessEntry?.primary_next_step ?? connection.primaryNextStep,
      proofSource: freshnessEntry?.proof_source ?? 'exchange_api_connections',
      lastSyncImportedCount: connection.lastSyncImportedCount,
      lastSyncSkippedCount: connection.lastSyncSkippedCount,
      lastSyncErrorCount: connection.lastSyncErrorCount,
      lastSyncSourceLabel: connection.lastSyncSourceLabel,
    });

    return {
      ...connection,
      freshness: freshnessEntry,
      health,
    };
  });
}

function buildSetupCoverageSummary({
  wallets,
  walletsStatus,
  importJobs,
  importStatus,
  exchangeConnections,
  exchangeStatus,
  freshness,
  freshnessStatus,
  setupHandoff,
}: PortfolioCoverageSummaryInput): PortfolioCoverageSummary {
  const csvSetupHandoff = setupHandoff?.kind === 'csv-import' ? setupHandoff : null;
  const apiSetupHandoff = setupHandoff?.kind === 'api-sync' ? setupHandoff : null;

  const latestWallet = sortByMostRecent(wallets)[0] ?? null;
  const walletLine: CoverageProofLine =
    wallets.length > 0
      ? {
          key: 'wallets',
          headline: 'Wallets included',
          status: 'included',
          tone: 'success',
          meta: [
            `${formatCount(wallets.length)} connected`,
            `latest ${formatProofTimestamp(latestWallet?.updated_at ?? latestWallet?.created_at)}`,
          ].join(' · '),
        }
      : walletsStatus === 'loading'
        ? {
            key: 'wallets',
            headline: 'Wallets unproven',
            status: 'unproven',
            tone: 'info',
            meta: 'checking saved wallets…',
          }
        : walletsStatus === 'error'
          ? {
              key: 'wallets',
              headline: 'Wallets unproven',
              status: 'unproven',
              tone: 'warning',
              meta: 'wallet proof unavailable',
            }
          : {
              key: 'wallets',
              headline: 'Wallets unproven',
              status: 'unproven',
              tone: 'info',
              meta: '0 connected · add wallets to prove onchain coverage',
            };

  const sortedJobs = sortByMostRecent(importJobs);
  const latestImportAttempt = sortedJobs[0] ?? null;
  const latestSuccessfulImport = sortedJobs.find(
    (job) => job.status === 'complete' && (job.imported_count ?? 0) > 0,
  ) ?? null;
  const csvNeedsReview = latestImportAttempt
    ? latestImportAttempt.status !== 'complete' ||
      (latestImportAttempt.imported_count ?? 0) === 0 ||
      (latestImportAttempt.skipped_count ?? 0) > 0 ||
      (latestImportAttempt.error_count ?? 0) > 0
    : csvSetupHandoff
      ? csvSetupHandoff.imported === 0 || csvSetupHandoff.skipped > 0 || csvSetupHandoff.errors > 0
      : false;

  let csvLine: CoverageProofLine;
  if (latestSuccessfulImport) {
    csvLine = {
      key: 'csv',
      headline: 'CSV imports included',
      status: 'included',
      tone: 'success',
      meta: [
        SOURCE_LABELS[latestSuccessfulImport.source] ?? latestSuccessfulImport.source,
        `imported ${formatCount(latestSuccessfulImport.imported_count)}`,
        `skipped ${formatCount(latestSuccessfulImport.skipped_count)}`,
        `errors ${formatCount(latestSuccessfulImport.error_count)}`,
        formatProofTimestamp(latestSuccessfulImport.completed_at ?? latestSuccessfulImport.created_at),
        csvSetupHandoff ? buildCsvSessionMeta(csvSetupHandoff) : null,
      ].filter(Boolean).join(' · '),
    };
  } else if (csvSetupHandoff && csvSetupHandoff.imported > 0) {
    csvLine = {
      key: 'csv',
      headline: 'CSV imports included',
      status: 'included',
      tone: 'info',
      meta: buildCsvSessionMeta(csvSetupHandoff),
    };
  } else if (latestImportAttempt) {
    csvLine = {
      key: 'csv',
      headline: 'CSV imports unproven',
      status: 'unproven',
      tone: latestImportAttempt.status === 'failed' || (latestImportAttempt.imported_count ?? 0) === 0 ? 'warning' : 'info',
      meta: [
        `latest ${SOURCE_LABELS[latestImportAttempt.source] ?? latestImportAttempt.source}`,
        `status ${latestImportAttempt.status}`,
        `imported ${formatCount(latestImportAttempt.imported_count)}`,
        `skipped ${formatCount(latestImportAttempt.skipped_count)}`,
        `errors ${formatCount(latestImportAttempt.error_count)}`,
        formatProofTimestamp(latestImportAttempt.completed_at ?? latestImportAttempt.created_at),
      ].join(' · '),
    };
  } else if (importStatus === 'loading') {
    csvLine = {
      key: 'csv',
      headline: 'CSV imports unproven',
      status: 'unproven',
      tone: 'info',
      meta: 'checking recent import history…',
    };
  } else if (importStatus === 'error') {
    csvLine = {
      key: 'csv',
      headline: 'CSV imports unproven',
      status: 'unproven',
      tone: 'warning',
      meta: 'import history unavailable',
    };
  } else {
    csvLine = {
      key: 'csv',
      headline: 'CSV imports unproven',
      status: 'unproven',
      tone: 'info',
      meta: 'no confirmed CSV import proof',
    };
  }

  const annotatedConnections = buildAnnotatedConnections(exchangeConnections, freshness);
  const successfulConnections = annotatedConnections.filter((connection) => Boolean(connection.health.lastSuccessfulSyncAt));
  const staleConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'stale');
  const freshConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'healthy');
  const blockedConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'blocked');
  const unsyncedConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'never_synced');
  const latestExchangeSuccess = sortByMostRecent(successfulConnections)[0] ?? null;
  const hasExchangeActivity = exchangeConnections.length > 0 || Boolean(apiSetupHandoff);

  let exchangeLine: CoverageProofLine;
  if (staleConnections.length > 0) {
    const stalestConnection = sortByMostRecent(staleConnections)[0];
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges stale',
      status: 'stale',
      tone: 'warning',
      meta: [
        `${formatCount(staleConnections.length)} stale`,
        freshConnections.length > 0 ? `${formatCount(freshConnections.length)} fresh` : null,
        latestExchangeSuccess ? `last ${formatProofTimestamp(latestExchangeSuccess.health.lastSuccessfulSyncAt)}` : null,
        stalestConnection ? formatFreshnessAge(stalestConnection.health.staleHours) : null,
      ].filter(Boolean).join(' · '),
    };
  } else if (successfulConnections.length > 0) {
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges included',
      status: 'included',
      tone: blockedConnections.length > 0 || unsyncedConnections.length > 0 ? 'info' : 'success',
      meta: [
        `${formatCount(successfulConnections.length)} synced`,
        latestExchangeSuccess ? `last ${formatProofTimestamp(latestExchangeSuccess.health.lastSuccessfulSyncAt)}` : null,
        blockedConnections.length > 0 ? `${formatCount(blockedConnections.length)} blocked` : null,
        unsyncedConnections.length > 0 ? `${formatCount(unsyncedConnections.length)} unproven` : null,
        apiSetupHandoff ? buildApiSessionMeta(apiSetupHandoff) : null,
      ].filter(Boolean).join(' · '),
    };
  } else if (apiSetupHandoff && apiSetupHandoff.imported > 0 && exchangeStatus !== 'error') {
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges included',
      status: 'included',
      tone: 'info',
      meta: buildApiSessionMeta(apiSetupHandoff),
    };
  } else if (exchangeConnections.length > 0) {
    const latestExchangeAttempt = sortByMostRecent(annotatedConnections)[0] ?? null;
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges unproven',
      status: 'unproven',
      tone: blockedConnections.length > 0 ? 'error' : 'info',
      meta: [
        `${formatCount(exchangeConnections.length)} saved`,
        '0 successful syncs',
        getExchangeTaxonomyLabel({
          blockerClassification: blockedConnections[0]?.health.blockerClassification ?? null,
          freshnessState: blockedConnections[0]?.health.freshnessState ?? null,
        }),
        latestExchangeAttempt ? `added ${formatProofTimestamp(latestExchangeAttempt.createdAt)}` : null,
        blockedConnections[0]?.errorMessage ?? null,
      ].filter(Boolean).join(' · '),
    };
  } else if (exchangeStatus === 'loading' || freshnessStatus === 'loading') {
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges unproven',
      status: 'unproven',
      tone: 'info',
      meta: 'checking exchange sync proof…',
    };
  } else if (exchangeStatus === 'error' || freshnessStatus === 'error') {
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges unproven',
      status: 'unproven',
      tone: 'warning',
      meta: 'exchange proof unavailable',
    };
  } else {
    exchangeLine = {
      key: 'exchanges',
      headline: 'Exchanges unproven',
      status: 'unproven',
      tone: 'info',
      meta: 'no completed exchange sync proof',
    };
  }

  const lines = [walletLine, csvLine, exchangeLine].sort((left, right) => {
    const statusOrder = { included: 0, stale: 1, unproven: 2 };
    const keyOrder = { wallets: 0, csv: 1, exchanges: 2 };
    return statusOrder[left.status] - statusOrder[right.status] || keyOrder[left.key] - keyOrder[right.key];
  });

  const retryableExchange =
    staleConnections[0] ??
    blockedConnections[0] ??
    unsyncedConnections[0] ??
    null;

  const hasIncludedCoverage = lines.some((line) => line.status === 'included');
  const action: CoverageSummaryAction | undefined = retryableExchange && hasExchangeActivity
    ? retryableExchange.health.primaryNextStep === 'manage_connection'
      ? {
          kind: 'manage-exchange',
          label: 'Manage Exchange',
          connectionId: retryableExchange.id,
          exchange: retryableExchange.label ?? retryableExchange.exchange,
        }
      : {
          kind: 'retry-sync',
          label: retryableExchange.health.primaryNextStep === 'sync_now' ? 'Sync Now' : 'Retry Sync',
          connectionId: retryableExchange.id,
          exchange: retryableExchange.label ?? retryableExchange.exchange,
        }
    : csvNeedsReview
      ? {
          kind: 'review-import',
          label: 'Review Import',
        }
      : !hasIncludedCoverage
        ? {
            kind: 'review-wallets',
            label: 'Review Wallets',
          }
        : {
            kind: 'inspect-analytics',
            label: 'Inspect Analytics',
          };

  const tone: PortfolioCoverageSummary['tone'] =
    lines.some((line) => line.tone === 'error')
      ? 'error'
      : lines.some((line) => line.status === 'stale' || line.tone === 'warning')
        ? 'warning'
        : lines.every((line) => line.status === 'included')
          ? 'success'
          : 'info';

  return {
    surface: 'setup',
    tone,
    title: 'Portfolio coverage summary',
    message: 'Included sources, freshness, and unresolved gaps for the current portfolio view.',
    lines,
    action,
    hasProof: false,
  };
}

function buildHandoffCandidate(setupHandoff: SetupHandoffContext | null): MonitoringProofCandidate | null {
  if (!setupHandoff) return null;

  if (setupHandoff.kind === 'csv-import') {
    return {
      kind: 'csv-import',
      sourceLabel: setupHandoff.sourceLabel,
      occurredAt: setupHandoff.occurredAt,
      imported: setupHandoff.imported,
      skipped: setupHandoff.skipped,
      errors: setupHandoff.errors,
      persisted: false,
    };
  }

  if (setupHandoff.kind === 'api-sync') {
    return {
      kind: 'api-sync',
      sourceLabel: setupHandoff.exchangeLabel,
      occurredAt: setupHandoff.occurredAt,
      imported: setupHandoff.imported,
      skipped: setupHandoff.skipped,
      errors: setupHandoff.errors,
      blocker: setupHandoff.blocker === 'SAFE_EXCHANGE_REPLAY_BLOCKED' ? setupHandoff.blocker : null,
      persisted: false,
    };
  }

  return null;
}

function buildMonitoringCoverageSummary(input: PortfolioCoverageSummaryInput): PortfolioCoverageSummary | null {
  const {
    wallets,
    walletsStatus,
    importJobs,
    importStatus,
    exchangeConnections,
    exchangeStatus,
    freshness,
    freshnessStatus,
    setupHandoff,
  } = input;

  const annotatedConnections = buildAnnotatedConnections(exchangeConnections, freshness);
  const successfulConnections = annotatedConnections.filter((connection) => Boolean(connection.health.lastSuccessfulSyncAt));
  const healthyConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'healthy');
  const staleConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'stale');
  const blockedConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'blocked');
  const unsyncedConnections = annotatedConnections.filter((connection) => connection.health.healthState === 'never_synced');
  const latestCompletedImport = sortByMostRecent(
    importJobs.filter((job) => job.status === 'complete'),
  )[0] ?? null;
  const latestExchangeSuccess = sortByMostRecent(successfulConnections)[0] ?? null;

  const persistedCandidates: MonitoringProofCandidate[] = [];
  if (latestCompletedImport?.completed_at) {
    persistedCandidates.push({
      kind: 'csv-import',
      sourceLabel:
        latestCompletedImport.reconciliation_source_label ??
        SOURCE_LABELS[latestCompletedImport.source] ??
        latestCompletedImport.source,
      occurredAt:
        latestCompletedImport.latest_ledger_imported_at ??
        latestCompletedImport.completed_at,
      imported: latestCompletedImport.imported_count ?? 0,
      skipped: latestCompletedImport.skipped_count ?? 0,
      errors: latestCompletedImport.error_count ?? 0,
      persisted: true,
    });
  }

  if (latestExchangeSuccess?.health.lastSuccessfulSyncAt) {
    persistedCandidates.push({
      kind: 'api-sync',
      sourceLabel:
        latestExchangeSuccess.health.lastSyncSourceLabel ??
        latestExchangeSuccess.lastSyncSourceLabel ??
        `${getExchangeRouteLabel(latestExchangeSuccess)} exchange sync`,
      occurredAt: latestExchangeSuccess.health.lastSuccessfulSyncAt,
      imported:
        latestExchangeSuccess.health.lastSyncImportedCount ??
        latestExchangeSuccess.lastSyncImportedCount ??
        0,
      skipped:
        latestExchangeSuccess.health.lastSyncSkippedCount ??
        latestExchangeSuccess.lastSyncSkippedCount ??
        0,
      errors:
        latestExchangeSuccess.health.lastSyncErrorCount ??
        latestExchangeSuccess.lastSyncErrorCount ??
        0,
      healthState: latestExchangeSuccess.health.healthState,
      blocker: latestExchangeSuccess.health.blockerCode,
      primaryNextStep: latestExchangeSuccess.health.primaryNextStep,
      connectionId: latestExchangeSuccess.id,
      exchange: latestExchangeSuccess.label ?? latestExchangeSuccess.exchange,
      persisted: true,
    });
  }

  const persistedCandidate =
    [...persistedCandidates].sort((left, right) => getTimestampValue(right.occurredAt) - getTimestampValue(left.occurredAt))[0] ?? null;
  const handoffCandidate = buildHandoffCandidate(setupHandoff);
  const handoffIsFreshest =
    Boolean(handoffCandidate) &&
    getTimestampValue(handoffCandidate?.occurredAt) > getTimestampValue(persistedCandidate?.occurredAt);
  const isReloadingProof =
    importStatus === 'loading' ||
    exchangeStatus === 'loading' ||
    freshnessStatus === 'loading';
  const shouldBridgeWithHandoff =
    handoffIsFreshest &&
    isReloadingProof;
  const activeCandidate = handoffIsFreshest
    ? handoffCandidate
    : persistedCandidate ?? handoffCandidate;

  if (!activeCandidate) {
    const hasAnyBaselineData =
      wallets.length > 0 ||
      importJobs.length > 0 ||
      exchangeConnections.length > 0 ||
      freshness.length > 0;
    if (!hasAnyBaselineData && (walletsStatus === 'loading' || importStatus === 'loading' || exchangeStatus === 'loading' || freshnessStatus === 'loading')) {
      return {
        surface: 'monitoring',
        tone: 'info',
        title: 'Portfolio refresh proof',
        message: 'Loading the latest monitoring proof…',
        isSkeleton: true,
        hasProof: false,
      };
    }

    return null;
  }

  const csvIncluded =
    Boolean(latestCompletedImport) ||
    (shouldBridgeWithHandoff && activeCandidate.kind === 'csv-import' && activeCandidate.imported > 0);
  const exchangeIncluded =
    successfulConnections.length > 0 ||
    (shouldBridgeWithHandoff && activeCandidate.kind === 'api-sync' && activeCandidate.imported > 0);
  const coverageIncludedCount = [wallets.length > 0, csvIncluded, exchangeIncluded].filter(Boolean).length;
  const coverageMissingCount = 3 - coverageIncludedCount;

  const gaps: MonitoringGap[] = [];
  if (wallets.length === 0) {
    gaps.push({ key: 'wallets', label: 'Wallets', href: '/wallet-hub' });
  }
  if (!csvIncluded) {
    gaps.push({ key: 'csv', label: 'CSV Imports', href: '/import' });
  }

  if (blockedConnections.length === 1) {
    const connection = blockedConnections[0];
    gaps.push({
      key: 'exchanges',
      label: getExchangeRouteLabel(connection),
      href: '/exchange-sync',
      connectionId: connection.id,
      exchange: connection.label ?? connection.exchange,
    });
  } else if (staleConnections.length === 1 && blockedConnections.length === 0) {
    const connection = staleConnections[0];
    gaps.push({
      key: 'exchanges',
      label: getExchangeRouteLabel(connection),
      href: '/exchange-sync',
      connectionId: connection.id,
      exchange: connection.label ?? connection.exchange,
    });
  } else if (unsyncedConnections.length === 1 && successfulConnections.length === 0 && blockedConnections.length === 0) {
    const connection = unsyncedConnections[0];
    gaps.push({
      key: 'exchanges',
      label: getExchangeRouteLabel(connection),
      href: '/exchange-sync',
      connectionId: connection.id,
      exchange: connection.label ?? connection.exchange,
    });
  } else if (!exchangeIncluded || blockedConnections.length > 1 || staleConnections.length > 1) {
    gaps.push({ key: 'exchanges', label: 'Exchanges', href: '/exchange-sync' });
  }

  if (
    activeCandidate.kind === 'csv-import' &&
    (activeCandidate.skipped > 0 || activeCandidate.errors > 0) &&
    !gaps.some((gap) => gap.key === 'csv')
  ) {
    gaps.push({ key: 'csv', label: activeCandidate.sourceLabel, href: '/import' });
  }

  const improvementEvidence =
    activeCandidate.kind === 'api-sync'
      ? activeCandidate.healthState === 'healthy'
      : activeCandidate.imported > 0;
  const outcome: 'improved' | 'unchanged' | 'still blocked' =
    gaps.length > 0 || activeCandidate.blocker === 'SAFE_EXCHANGE_REPLAY_BLOCKED'
      ? 'still blocked'
      : improvementEvidence
        ? 'improved'
        : 'unchanged';

  const action: CoverageSummaryAction =
    gaps.length === 1
      ? {
          kind: 'review-source',
          label: getMonitoringActionLabel(gaps[0]),
          href: gaps[0].href,
          sourceKey: gaps[0].key,
          connectionId: gaps[0].connectionId,
          exchange: gaps[0].exchange,
        }
      : gaps.length > 1
        ? getAggregateCoverageAction(gaps)
        : {
            kind: 'review-portfolio',
            label: 'Review Portfolio',
          };

  const freshnessValue =
    activeCandidate.kind === 'api-sync'
      ? [
          activeCandidate.healthState === 'healthy'
            ? 'healthy now'
            : activeCandidate.healthState === 'stale'
              ? 'stale now'
              : activeCandidate.healthState === 'blocked'
                ? 'blocked now'
                : activeCandidate.healthState === 'never_synced'
                  ? 'never synced'
                  : null,
          healthyConnections.length > 0 ? `${formatCount(healthyConnections.length)} fresh` : null,
          staleConnections.length > 0 ? `${formatCount(staleConnections.length)} stale` : null,
          blockedConnections.length > 0 ? `${formatCount(blockedConnections.length)} blocked` : null,
          unsyncedConnections.length > 0 ? `${formatCount(unsyncedConnections.length)} never synced` : null,
        ].filter(Boolean).join(' · ')
      : healthyConnections.length + staleConnections.length + blockedConnections.length + unsyncedConnections.length > 0
        ? [
            healthyConnections.length > 0 ? `${formatCount(healthyConnections.length)} fresh` : null,
            staleConnections.length > 0 ? `${formatCount(staleConnections.length)} stale` : null,
            blockedConnections.length > 0 ? `${formatCount(blockedConnections.length)} blocked` : null,
            unsyncedConnections.length > 0 ? `${formatCount(unsyncedConnections.length)} never synced` : null,
          ].filter(Boolean).join(' · ')
        : 'no exchange freshness proof';
  const coverageValue = [
    `${activeCandidate.imported > 0 ? '+' : ''}${formatCount(activeCandidate.imported)} rows`,
    `${formatCount(coverageIncludedCount)} included`,
    `${formatCount(coverageMissingCount)} missing`,
  ].join(' · ');
  const blockerTruth = getBlockerTruthFactValue({
    activeCandidate,
    blockedConnections,
    staleConnections,
    gaps,
  });

  const hasRefreshFailure =
    walletsStatus === 'error' ||
    importStatus === 'error' ||
    exchangeStatus === 'error' ||
    freshnessStatus === 'error';
  const helper: CoverageSummaryHelper | undefined = shouldBridgeWithHandoff
    ? {
        tone: 'info',
        eyebrow: 'Refreshing latest proof',
        message: 'Using the latest handoff while persisted monitoring proof rehydrates on this route.',
      }
    : hasRefreshFailure
      ? {
          tone: 'warning',
          eyebrow: 'Latest proof refresh delayed',
          message: 'Showing the last truthful proof while one or more monitoring checks recover.',
        }
      : isReloadingProof
        ? {
            tone: 'info',
            eyebrow: 'Refreshing latest proof',
            message: 'Keeping the last truthful proof mounted while Pinecurl rechecks freshness and source coverage.',
          }
        : undefined;

  return {
    surface: 'monitoring',
    tone: outcome === 'improved' ? 'success' : outcome === 'still blocked' ? 'warning' : 'info',
    title: 'Portfolio refresh proof',
    message:
      outcome === 'still blocked'
        ? blockerTruth.value
        : outcome === 'improved'
          ? 'Carried source proof shows monitoring improved here while deeper detail stays on the owner route.'
          : 'Carried source proof remains mounted here while coverage stays unchanged on this route.',
    facts: [
      {
        key: 'latest-source',
        label: 'Latest source',
        value: activeCandidate.sourceLabel,
      },
      {
        key: 'completed-at',
        label: 'Completed',
        value: formatProofTimestamp(activeCandidate.occurredAt),
        mono: true,
      },
      {
        key: 'outcome',
        label: 'Outcome',
        value: outcome,
        tone: outcome === 'improved' ? 'success' : outcome === 'still blocked' ? 'warning' : 'info',
      },
      {
        key: 'freshness-delta',
        label: 'Freshness',
        value: freshnessValue,
        mono: true,
      },
      {
        key: 'coverage-delta',
        label: 'Coverage',
        value: coverageValue,
        mono: true,
      },
    ],
    action,
    helper,
    hasProof: true,
  };
}

export function buildPortfolioCoverageSummary(
  input: PortfolioCoverageSummaryInput,
): PortfolioCoverageSummary {
  if (input.surface === 'monitoring') {
    const monitoringSummary = buildMonitoringCoverageSummary(input);
    if (monitoringSummary) {
      return monitoringSummary;
    }
  }

  return buildSetupCoverageSummary(input);
}
